import { Node, mergeAttributes } from "@tiptap/core";

/**
 * Round-trips container elements the schema has no node for.
 *
 * A schema is a closed vocabulary: `<div>`, `<section>`, `<dl>` and friends
 * have no node, so by default they are unwrapped and their markup is lost on
 * save. That is fatal for the factsheet, where the layout IS the markup - see
 * factsheetHtml.js.
 *
 * Rather than a node per tag, one node stores the tag name as an attribute and
 * renders it back. The catch is ProseMirror's content model, which is fixed per
 * node type and cannot vary by instance - so there are two:
 *
 *   genericBlock      holds block children   <div><p>..</p></div>
 *   genericTextBlock  holds inline content   <dt>Seat</dt>
 *
 * The parse rules discriminate on whether the element actually contains block
 * children, so `<dt>Seat</dt>` keeps its text directly instead of gaining a
 * wrapping paragraph.
 */

const TAGS = [
  "div",
  "section",
  "article",
  "header",
  "footer",
  "aside",
  "main",
  "nav",
  "figure",
  "figcaption",
  "dl",
  "dt",
  "dd",
  "address",
];

// Anything that cannot sit inside inline content.
const BLOCK_TAGS = [
  "p", "div", "section", "article", "header", "footer", "aside", "main", "nav",
  "figure", "figcaption", "dl", "dt", "dd", "address", "ul", "ol", "li",
  "table", "thead", "tbody", "tr", "td", "th", "blockquote", "pre", "hr",
  "h1", "h2", "h3", "h4", "h5", "h6",
];

const hasBlockChild = (element) =>
  Array.from(element.children || []).some((child) =>
    BLOCK_TAGS.indexOf(String(child.tagName || "").toLowerCase()) !== -1
  );

const tagAttribute = {
  tag: {
    default: "div",
    parseHTML: (element) => String(element.tagName || "div").toLowerCase(),
    // Rendered as the element itself, not as an attribute on it.
    renderHTML: () => ({}),
  },
};

const renderAsTag = ({ node, HTMLAttributes }) => [
  node.attrs.tag || "div",
  mergeAttributes(HTMLAttributes),
  0,
];

export const GenericBlock = Node.create({
  name: "genericBlock",
  group: "block",
  content: "block+",
  // Keeps the wrapper from being dissolved when the selection spans it.
  defining: true,

  addOptions() {
    return { tags: TAGS };
  },

  addAttributes() {
    return tagAttribute;
  },

  parseHTML() {
    return this.options.tags.map((tag) => ({
      tag,
      getAttrs: (element) => (hasBlockChild(element) ? {} : false),
    }));
  },

  renderHTML: renderAsTag,
});

export const GenericTextBlock = Node.create({
  name: "genericTextBlock",
  group: "block",
  content: "inline*",
  defining: true,

  addOptions() {
    return { tags: TAGS };
  },

  addAttributes() {
    return tagAttribute;
  },

  parseHTML() {
    return this.options.tags.map((tag) => ({
      tag,
      getAttrs: (element) => (hasBlockChild(element) ? false : {}),
    }));
  },

  renderHTML: renderAsTag,
});

export default GenericBlock;
